import { useEscClose } from '../hooks/useEscClose';
import AdminModuleUnlockModal from '../components/AdminModuleUnlockModal';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Users, User, UserPlus, Trash2, X, Loader2, Search, BookOpen, ChevronDown, ChevronUp, Archive, ArchiveRestore, History, Stethoscope, FileDown, FileText
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const AZAV_VARIANT_COURSES = ['course-behandlungspflege-lg', 'course-ambulanter-pflegeassistent', 'course-pflegehilfskraft'];

const getShortCourseTitle = (title) => {
  if (!title) return '';
  const map = {
    'Chronisch und schwerheilende Wunden für Leitung': 'Wunden (Leitung)',
    'Chronisch schwerheilende Wunden für Pflegefachkräfte': 'Wunden (Pflege)',
    'Weiterbildung zum/zur Wundtherapeut/in': 'Wundtherapeut',
    'Weiterbildung Wundexperte': 'Wundexperte',
    'PDL-Weiterbildung Teil 1': 'PDL Teil 1',
    'PDL-Weiterbildung Teil 2': 'PDL Teil 2',
    'Behandlungspflege LG1+LG2': 'Behandlungspflege',
    'Weiterbildung Palliativpflege': 'Palliativpflege',
    'Weiterbildung zur Praxisanleitung': 'Praxisanleitung',
    'Pflegeberater nach §7a SGB XI': 'Pflegeberater',
    'Hygienebeauftragter in der Pflege': 'Hygienebeauftragter',
    'Gerontopsychiatrische Fachweiterbildung': 'Gerontopsychiatrie',
    'Hygiene und Infektionsprävention': 'Hygiene',
    'Haftung, Verantwortung und Delegation': 'Haftung',
    'Gewaltprävention und Deeskalation': 'Gewaltprävention',
    'Kommunikation mit Angehörigen und im Team': 'Kommunikation',
  };
  for (const [full, short] of Object.entries(map)) {
    if (title.includes(full)) return short;
  }
  return title.length > 20 ? title.substring(0, 18) + '...' : title;
};


const STATUS = {
  action: { color: '#E07A5F', label: 'Braucht Aktion' },
  inactive: { color: '#B4AA9C', label: 'Inaktiv' },
  active: { color: '#8F650F', label: 'Aktiv' },
};

const relActivity = (days) => {
  if (days === null || days === undefined) return '—';
  if (days === 0) return 'heute';
  if (days === 1) return 'gestern';
  return `vor ${days} Tagen`;
};

const RATING_OPTS = [
  { value: 'bestanden', label: 'Bestanden' },
  { value: 'ueberarbeiten', label: 'Überarbeiten' },
  { value: 'nicht_bestanden', label: 'Nicht bestanden' },
];

const TASK_TYPE_LABELS = { fallbeispiel: 'Fallbeispiel', reflexion: 'Reflexion', wissensfrage: 'Wissensfrage', praxisaufgabe: 'Praxisaufgabe' };

// Schnellkorrektur: eingereichte Aufgaben direkt im Drawer lesen + Feedback schreiben
const DrawerTaskFeedback = ({ userId, getAuthHeader, onFeedbackSent }) => {
  const [subs, setSubs] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [draft, setDraft] = useState({});
  const [rating, setRating] = useState({});
  const [sending, setSending] = useState(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/users/${userId}/task-submissions`, getAuthHeader());
      setSubs(res.data.submissions || []);
    } catch { setSubs([]); }
  };

  useEffect(() => { setSubs(null); setOpenId(null); load(); /* eslint-disable-next-line */ }, [userId]);

  const send = async (s) => {
    const fb = (draft[s.id] || '').trim();
    if (!fb) { toast.error('Bitte erst Feedback schreiben'); return; }
    setSending(s.id);
    try {
      await axios.post(`${API}/admin/task-feedback`, {
        user_id: userId,
        module_id: s.module_id,
        task_id: s.task_id,
        feedback: fb,
        rating: rating[s.id] || 'bestanden',
      }, getAuthHeader());
      toast.success('Feedback gespeichert');
      setDraft((d) => ({ ...d, [s.id]: '' }));
      await load();
      onFeedbackSent?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    }
    setSending(null);
  };

  if (subs === null) return <div className="py-2"><Loader2 className="w-4 h-4 animate-spin text-terra" /></div>;
  if (subs.length === 0) return <p className="text-[13px] text-sand-500 italic">Noch keine eingereichten Aufgaben.</p>;

  const pending = subs.filter((s) => !s.reviewed_by_admin);
  const ordered = [...pending, ...subs.filter((s) => s.reviewed_by_admin)];

  return (
    <div className="space-y-2" data-testid="drawer-task-list">
      {pending.length > 0 && (
        <p className="text-[12px] font-semibold" style={{ color: '#E07A5F' }}>{pending.length} warten auf dein Feedback</p>
      )}
      {ordered.map((s) => {
        const isOpen = openId === s.id;
        return (
          <div key={s.id} className="border border-cream-300 rounded-xl overflow-hidden bg-cream-100">
            <button
              onClick={() => setOpenId(isOpen ? null : s.id)}
              className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left hover:bg-cream-200 transition-colors"
              data-testid={`task-item-${s.id}`}
            >
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: s.reviewed_by_admin ? '#D89232' : '#E07A5F' }} />
              <div className="flex-1 min-w-0">
                <p className="text-[12.5px] text-espresso truncate">{TASK_TYPE_LABELS[s.task_type] || s.task_type} · {s.module_title}</p>
                <p className="text-[10.5px] text-sand-500">
                  {s.submitted_at ? new Date(s.submitted_at).toLocaleDateString('de-DE') : ''}
                  {s.reviewed_by_admin ? ' · ✓ Feedback gegeben' : ' · offen'}
                </p>
              </div>
              <ChevronDown className={`w-3.5 h-3.5 text-sand-500 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            {isOpen && (
              <div className="px-3.5 pb-3.5 border-t border-cream-200">
                {/* Antwort des Teilnehmers */}
                <p className="font-mono text-[9px] uppercase font-semibold mt-3 mb-1.5" style={{ letterSpacing: '0.2em', color: '#8F650F' }}>Antwort</p>
                <p className="text-[13px] text-sand-800 leading-relaxed whitespace-pre-wrap max-h-48 overflow-y-auto pl-3" style={{ borderLeft: '2px solid #E0D6CA' }}>
                  {s.answer_text || '— keine Antwort gespeichert —'}
                </p>
                {/* Vorhandenes Feedback */}
                {s.admin_feedback && (
                  <>
                    <p className="font-mono text-[9px] uppercase font-semibold mt-3 mb-1.5" style={{ letterSpacing: '0.2em', color: '#8F650F' }}>
                      Dein Feedback {s.admin_rating ? `· ${(RATING_OPTS.find((r) => r.value === s.admin_rating) || {}).label || s.admin_rating}` : ''}
                    </p>
                    <p className="text-[13px] text-sand-700 italic leading-relaxed whitespace-pre-wrap pl-3" style={{ borderLeft: '2px solid #D89232' }}>{s.admin_feedback}</p>
                  </>
                )}
                {/* Feedback schreiben */}
                <textarea
                  value={draft[s.id] || ''}
                  onChange={(e) => setDraft((d) => ({ ...d, [s.id]: e.target.value }))}
                  placeholder={s.reviewed_by_admin ? 'Feedback ergänzen oder ersetzen…' : 'Feedback schreiben…'}
                  rows={3}
                  className="w-full mt-3 px-3 py-2 bg-cream-50 border border-cream-300 rounded-lg text-[13px] text-sand-800 placeholder:text-sand-400 focus:outline-none focus:border-terra resize-y"
                  data-testid={`task-feedback-input-${s.id}`}
                />
                <div className="flex items-center gap-2 mt-2">
                  <select
                    value={rating[s.id] || 'bestanden'}
                    onChange={(e) => setRating((r) => ({ ...r, [s.id]: e.target.value }))}
                    className="px-2 py-1.5 bg-cream-50 border border-cream-300 rounded-lg text-[12px] text-sand-800"
                    data-testid={`task-rating-${s.id}`}
                  >
                    {RATING_OPTS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                  <button
                    onClick={() => send(s)}
                    disabled={sending === s.id}
                    className="ml-auto inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[11px] font-bold uppercase transition-colors disabled:opacity-50"
                    style={{ border: '1.5px solid #E07A5F', color: '#E07A5F', letterSpacing: '0.12em' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#E07A5F'; e.currentTarget.style.color = '#F5F0EA'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#E07A5F'; }}
                    data-testid={`task-feedback-send-${s.id}`}
                  >
                    {sending === s.id && <Loader2 className="w-3 h-3 animate-spin" />}
                    Feedback senden
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const AdminUsersPage = ({ Layout, getAuthHeader }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [partMap, setPartMap] = useState({});
  const [feedbackUserIds, setFeedbackUserIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [view, setView] = useState('all');
  const [moreOpen, setMoreOpen] = useState(false);
  const [drawerUserId, setDrawerUserId] = useState(null);
  const [unlockModal, setUnlockModal] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  useEscClose(() => { setDrawerUserId(null); setShowCreateModal(false); });
  const [newUser, setNewUser] = useState({ username: '', password: '', name: '', email: '', birth_date: '', role: 'user' });
  const [newUserCourses, setNewUserCourses] = useState([]);
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);
  const [creating, setCreating] = useState(false);
  const [backfillModal, setBackfillModal] = useState(null);
  const [mlData, setMlData] = useState({ entries: [], open_requests: [] });

  useEffect(() => {
    if (!drawerUserId) { setMlData({ entries: [], open_requests: [] }); return; }
    axios.get(`${API}/admin/users/${drawerUserId}/modullisten`, getAuthHeader())
      .then((r) => setMlData(r.data)).catch(() => {});
  }, [drawerUserId]);

  const kursBeendet = async (u, cid, title) => {
    if (!window.confirm(`"${title}" für ${u.name || u.username} als beendet markieren?\n\nAlle Module werden als bearbeitet markiert und der Teilnehmer wird zur Online-Unterschrift der Bearbeitungsbestätigung aufgefordert.`)) return;
    try {
      const r = await axios.post(`${API}/admin/modulliste/kurs-beendet`, { user_id: u.id, course_id: cid }, getAuthHeader());
      toast.success(r.data.email_sent
        ? `${r.data.message} — E-Mail zur Unterschrift gesendet`
        : `${r.data.message} — Teilnehmer sieht die Aufforderung im Portal`);
      axios.get(`${API}/admin/users/${u.id}/modullisten`, getAuthHeader()).then((res) => setMlData(res.data));
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const downloadBestaetigung = async (entry) => {
    try {
      const r = await axios.get(`${API}/admin/modulliste/${entry.id}/pdf`, { ...getAuthHeader(), responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `Bearbeitungsbestaetigung_${(entry.user_name || '').replace(/\s+/g, '_')}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Fehler beim Download'); }
  };

  const [diagnoseModal, setDiagnoseModal] = useState(null);
  const searchRef = useRef(null);

  // URL params: ?q= (Suche) und ?view= (vorgefilterter Blick)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('q');
    const v = params.get('view');
    if (q) setSearchQuery(q);
    if (v) setView(v);
  }, [location.search]);

  useEffect(() => { loadData(); }, []);
  useEffect(() => { searchRef.current?.focus(); }, [loading]);

  const loadData = async () => {
    try {
      const [usersRes, coursesRes, partsRes, feedRes] = await Promise.all([
        axios.get(`${API}/admin/users?include_archived=all`, getAuthHeader()),
        axios.get(`${API}/courses`),
        axios.get(`${API}/admin/all-participants`, getAuthHeader()).catch(() => ({ data: { participants: [] } })),
        axios.get(`${API}/admin/tasks/pending-feedback`, getAuthHeader()).catch(() => ({ data: { items: [] } })),
      ]);
      const seenIds = new Set();
      setUsers(usersRes.data.filter((u) => !seenIds.has(u.id) && seenIds.add(u.id)));
      setCourses(coursesRes.data);
      const map = {};
      (partsRes.data.participants || []).forEach((p) => { map[p.user_id] = p; });
      setPartMap(map);
      setFeedbackUserIds(new Set((feedRes.data.items || []).map((i) => i.user_id)));
    } catch (error) {
      toast.error('Fehler beim Laden');
    }
    setLoading(false);
  };

  // ---------- Aktionen (unverändert übernommen) ----------
  const archiveUser = async (userId, userName) => {
    if (!window.confirm(`"${userName}" wirklich archivieren?\n\nDer Account wird gesperrt und aus den Standard-Listen entfernt. Alle Daten bleiben erhalten.`)) return;
    try {
      await axios.put(`${API}/admin/users/${userId}/archive`, {}, getAuthHeader());
      toast.success(`${userName} archiviert`);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Archivieren'); }
  };

  const unarchiveUser = async (userId, userName) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/unarchive`, {}, getAuthHeader());
      toast.success(`${userName} reaktiviert`);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Reaktivieren'); }
  };

  const deleteUser = async (userId, userName) => {
    if (!window.confirm(`"${userName}" wirklich löschen? Alle Daten werden entfernt.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, getAuthHeader());
      toast.success('Benutzer gelöscht');
      setDrawerUserId(null);
      loadData();
    } catch (error) { toast.error('Fehler beim Löschen'); }
  };

  const createUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const shouldSendMail = sendWelcomeEmail && !!newUser.email?.trim();
      const createRes = await axios.post(`${API}/admin/users?send_welcome_email=${shouldSendMail}`, newUser, getAuthHeader());
      const createdUserId = createRes.data?.id;
      const welcomeInfo = createRes.data?.welcome_email;
      let courseMsg = '';
      if (createdUserId && newUserCourses.length > 0) {
        await Promise.all(newUserCourses.map((cid) =>
          axios.post(`${API}/admin/assign-course`, { user_id: createdUserId, course_id: cid }, getAuthHeader()).catch(() => null)
        ));
        courseMsg = ` + ${newUserCourses.length} Kurs(e)`;
      }
      if (welcomeInfo) {
        if (welcomeInfo.success) toast.success(`Benutzer erstellt${courseMsg} · Willkommens-Mail an ${newUser.email} versendet ✉️`);
        else toast.warning(`Benutzer erstellt${courseMsg}, aber Willkommens-Mail fehlgeschlagen: ${welcomeInfo.error}`);
      } else {
        toast.success(`Benutzer erstellt${courseMsg}!`);
      }
      setShowCreateModal(false);
      setNewUser({ username: '', password: '', name: '', email: '', birth_date: '', role: 'user' });
      setNewUserCourses([]);
      setSendWelcomeEmail(true);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    }
    setCreating(false);
  };

  const toggleNewUserCourse = (cid) => {
    setNewUserCourses((prev) => prev.includes(cid) ? prev.filter((c) => c !== cid) : [...prev, cid]);
  };

  const assignCourse = async (userId, courseId, variant = null) => {
    try {
      await axios.post(`${API}/admin/assign-course`, { user_id: userId, course_id: courseId, variant }, getAuthHeader());
      toast.success(variant ? `Kurs zugewiesen (${variant === 'bg' ? 'BG-Maßnahme' : 'Selbstlernen'})!` : 'Kurs zugewiesen!');
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Fehler'); }
  };

  const setAssignmentVariant = async (userId, courseId, variant) => {
    try {
      await axios.put(`${API}/admin/assignment-variant`, { user_id: userId, course_id: courseId, variant }, getAuthHeader());
      toast.success(variant === 'bg' ? 'Auf BG-Maßnahme umgestellt — Tagesmaterial sichtbar' : 'Auf Selbstlernen umgestellt — ohne Tagesmaterial');
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const unassignCourse = async (userId, courseId) => {
    try {
      await axios.delete(`${API}/admin/unassign-course`, { ...getAuthHeader(), data: { user_id: userId, course_id: courseId } });
      toast.success('Zuweisung entfernt');
      loadData();
    } catch (error) { toast.error('Fehler'); }
  };

  const updateUserProfession = async (userId, profession) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/profession?profession=${encodeURIComponent(profession || '')}`, {}, getAuthHeader());
      toast.success(profession ? 'Berufsrolle gesetzt' : 'Berufsrolle entfernt');
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Speichern'); }
  };

  const updateUserName = async (userId, currentName) => {
    const next = window.prompt('Name korrigieren:', currentName || '');
    if (next === null) return;
    const trimmed = next.trim();
    if (trimmed.length < 2) { toast.error('Name muss mind. 2 Zeichen haben'); return; }
    try {
      await axios.put(`${API}/admin/users/${userId}/name?name=${encodeURIComponent(trimmed)}`, {}, getAuthHeader());
      toast.success('Name aktualisiert');
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Speichern'); }
  };

  const updateUserEmail = async (u) => {
    const newEmail = window.prompt(`E-Mail für ${u.name}:`, u.email || '');
    if (newEmail === null) return;
    try {
      await axios.put(`${API}/admin/users/${u.id}/email?email=${encodeURIComponent(newEmail.trim())}`, {}, getAuthHeader());
      toast.success(newEmail.trim() ? 'E-Mail aktualisiert' : 'E-Mail entfernt');
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Speichern'); }
  };

  const updateUserBirthDate = async (userId, currentBirthDate) => {
    const next = window.prompt('Geburtsdatum (JJJJ-MM-TT, leer = entfernen):', currentBirthDate || '');
    if (next === null) return;
    try {
      await axios.put(`${API}/admin/users/${userId}/birth-date?birth_date=${encodeURIComponent(next.trim())}`, {}, getAuthHeader());
      toast.success(next.trim() ? 'Geburtsdatum aktualisiert' : 'Geburtsdatum entfernt');
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Speichern'); }
  };

  const resetPassword = async (u) => {
    const pw = window.prompt(`Neues Passwort für ${u.name} (min. 4 Zeichen):`, `Lernzeit${new Date().getFullYear()}!`);
    if (pw === null) return;
    if ((pw || '').trim().length < 4) { toast.error('Passwort zu kurz'); return; }
    try {
      await axios.put(`${API}/admin/users/${u.id}/password?new_password=${encodeURIComponent(pw.trim())}`, {}, getAuthHeader());
      toast.success('Passwort zurückgesetzt');
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Zurücksetzen'); }
  };

  const sendWelcomeMailToExistingUser = async (u) => {
    if (!u.email) { toast.error('Erst eine E-Mail-Adresse setzen'); return; }
    const defaultPw = `Lernzeit${new Date().getFullYear()}!`;
    const pw = window.prompt(`Willkommens-Mail an ${u.email} senden:\n\nWelches Passwort soll in der Mail stehen?\nDas Passwort wird gleichzeitig zurückgesetzt.\n\n(Min. 4 Zeichen)`, defaultPw);
    if (pw === null) return;
    const trimmed = (pw || '').trim();
    if (trimmed.length < 4) { toast.error('Passwort zu kurz (min. 4 Zeichen)'); return; }
    try {
      await axios.post(`${API}/admin/users/${u.id}/send-welcome-email?password=${encodeURIComponent(trimmed)}&reset_password=true`, {}, getAuthHeader());
      toast.success(`Mail an ${u.email} verschickt + Passwort neu gesetzt`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Versand'); }
  };

  const sendReminder = async (u) => {
    try {
      const res = await axios.post(`${API}/admin/inactivity-reminders/send?days=14&user_id=${encodeURIComponent(u.id)}`, {}, getAuthHeader());
      if (res.data.sent > 0) toast.success(`Erinnerung an ${u.name} gesendet`);
      else toast.info('Keine Mail gesendet — kürzlich schon erinnert oder nicht inaktiv genug.');
    } catch (e) { toast.error('Erinnerung konnte nicht gesendet werden'); }
  };

  const issueCertificate = async (u, courseId, courseTitle) => {
    try {
      toast.info('Bescheinigung wird erstellt…');
      const res = await axios.post(`${API}/admin/certificate/generate`, { user_id: u.id, course_id: courseId, signature_data: '' }, getAuthHeader());
      const b64 = res.data.pdf_base64;
      const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', res.data.filename || `Bescheinigung_${u.name}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Bescheinigung ${res.data.certificate_number} ausgestellt`);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bescheinigung konnte nicht erstellt werden');
    }
  };

  const exportTasks = async (u, type) => {
    try {
      toast.info('Export wird erstellt…');
      const ep = type === 'pdf' ? 'export-user-tasks-pdf' : 'export-user-tasks';
      const res = await axios.get(`${API}/admin/${ep}/${u.id}`, { ...getAuthHeader(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Aufgaben_${(u.name || 'Teilnehmer').replace(/\s+/g, '_')}.${type === 'pdf' ? 'pdf' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export heruntergeladen');
    } catch (e) { toast.error('Fehler beim Export'); }
  };

  const downloadArbeitgeberBericht = async (u) => {
    try {
      toast.info('Bericht wird erstellt…');
      const res = await axios.get(`${API}/admin/users/${u.id}/arbeitgeber-bericht`, { ...getAuthHeader(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Fortbildungsbericht_${(u.name || 'Teilnehmer').replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Arbeitgeber-Bericht heruntergeladen');
    } catch (e) { toast.error('Fehler beim Erstellen des Berichts'); }
  };

  const assignAllJahresfortbildung = async (userId) => {
    try {
      const res = await axios.post(`${API}/admin/assign-all-jahresfortbildung?user_id=${userId}`, {}, getAuthHeader());
      toast.success(`${res.data.assigned} Jahresfortbildung-Kurse zugewiesen!`);
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Fehler'); }
  };

  const assignAllExpertenstandards = async (userId) => {
    try {
      const res = await axios.post(`${API}/admin/assign-all-expertenstandards?user_id=${userId}`, {}, getAuthHeader());
      toast.success(res.data.already_assigned > 0
        ? `${res.data.assigned} Expertenstandards zugewiesen (${res.data.already_assigned} waren schon zugewiesen)`
        : `Alle ${res.data.assigned} Expertenstandard-Kurse zugewiesen!`);
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Fehler'); }
  };

  const reseedCourses = async () => {
    if (!window.confirm('Kurs-Daten aus Code neu in die DB schreiben?\n\nIdempotent — keine Teilnehmerdaten gehen verloren.')) return;
    try {
      const r = await axios.get(`${API}/seed`, getAuthHeader());
      toast.success(`Kurs-Daten aktualisiert! ${r.data?.message || ''}`);
      loadData();
    } catch (e) { toast.error('Fehler beim Re-Seed: ' + (e.response?.data?.detail || e.message)); }
  };

  const formatBirthDate = (iso) => {
    if (!iso) return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
  };

  // ---------- Anreicherung: Fortschritt, Aktivität, Status ----------
  const enrich = (u) => {
    const p = partMap[u.id];
    const pCourses = p?.courses || [];
    const main = [...pCourses].sort((a, b) => (b.progress_percentage || 0) - (a.progress_percentage || 0))[0] || null;
    const minStuck = pCourses.length ? Math.min(...pCourses.map((c) => c.days_stuck ?? 999)) : null;
    const needsAction = pCourses.some((c) => c.needs_action && c.progress_percentage < 100);
    const readyCert = pCourses.some((c) => c.progress_percentage >= 100 && !c.certificate_issued);
    const inactive14 = minStuck !== null && minStuck >= 14;
    const status = needsAction ? 'action' : inactive14 ? 'inactive' : 'active';
    const ue = Math.round((p?.total_learning_time_minutes || 0) / 45);
    return { p, pCourses, main, minStuck, needsAction, readyCert, inactive14, status, ue };
  };

  const regularUsers = useMemo(() => users.filter((u) => u.role !== 'admin'), [users]);

  const filteredUsers = useMemo(() => {
    let base = regularUsers.filter((u) => !u.archived);
    if (view === 'archived') base = regularUsers.filter((u) => u.archived);
    else if (view === 'action') base = base.filter((u) => enrich(u).needsAction);
    else if (view === 'ready_cert') base = base.filter((u) => enrich(u).readyCert);
    else if (view === 'inactive') base = base.filter((u) => enrich(u).inactive14);
    else if (view === 'feedback') base = base.filter((u) => feedbackUserIds.has(u.id));
    else if (view.startsWith('course:')) {
      const cid = view.slice(7);
      base = base.filter((u) => u.assigned_courses?.includes(cid));
    }
    if (!searchQuery.trim()) return base;
    const q = searchQuery.toLowerCase();
    return base.filter((u) =>
      u.name?.toLowerCase().includes(q) ||
      u.username?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regularUsers, view, searchQuery, partMap, feedbackUserIds]);

  const drawerUser = drawerUserId ? users.find((u) => u.id === drawerUserId) : null;

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-terra" /></div></Layout>;
  }

  return (
    <Layout>
      <div className="max-w-5xl animate-fade-in" data-testid="admin-users-page">
        {/* Kopf */}
        <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="w-8 h-[2px] bg-terra" />
              <span className="text-[11px] uppercase tracking-[0.25em] font-bold text-terra">Administration · Teilnehmer</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl text-espresso leading-[1.05]">
              Teil<span className="italic text-terra">nehmer</span>.
            </h1>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 h-[42px] px-6 rounded-full text-[12px] font-bold uppercase transition-colors"
            style={{ border: '1.5px solid #E07A5F', color: '#E07A5F', letterSpacing: '0.14em' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#E07A5F'; e.currentTarget.style.color = '#F5F0EA'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#E07A5F'; }}
            data-testid="create-user-btn"
          >
            <UserPlus className="w-4 h-4" />
            Neuer Benutzer
          </button>
        </div>

        {/* Suche — der Hauptweg */}
        <div className="relative mb-4">
          <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-sand-500" />
          <input
            ref={searchRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Teilnehmer suchen…"
            className="w-full pl-14 pr-10 h-[54px] bg-cream-100 border border-cream-300 rounded-2xl text-[16px] text-sand-800 placeholder:text-sand-400 focus:outline-none focus:border-terra"
            data-testid="user-search-input"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute right-4 top-1/2 -translate-y-1/2 text-sand-500 hover:text-sand-800" data-testid="clear-search">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Ansicht + Weitere Filter */}
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          <label className="font-mono text-[10px] uppercase font-semibold text-sand-500" style={{ letterSpacing: '0.2em' }}>Ansicht</label>
          <select
            value={view}
            onChange={(e) => setView(e.target.value)}
            className="h-[38px] px-3 bg-cream-100 border border-cream-300 rounded-xl text-sm text-sand-800 focus:outline-none focus:border-terra min-w-[220px]"
            data-testid="view-select"
          >
            <option value="all">Alle</option>
            <option value="action">Braucht Aktion</option>
            <option value="ready_cert">Fertig zur Bescheinigung</option>
            <option value="inactive">Inaktiv &gt;14 Tage</option>
            <option value="feedback">Wartet auf Feedback</option>
            <option value="archived">Archiviert</option>
            <optgroup label="Nach Kurs…">
              {courses.map((c) => (
                <option key={c.id} value={`course:${c.id}`}>{getShortCourseTitle(c.title)}</option>
              ))}
            </optgroup>
          </select>

          <div className="relative">
            <button
              onClick={() => setMoreOpen(!moreOpen)}
              className="h-[38px] px-4 bg-cream-100 border border-cream-300 rounded-xl text-sm text-sand-700 hover:bg-cream-200 transition-colors flex items-center gap-2"
              data-testid="more-filters-btn"
            >
              Weitere Filter
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${moreOpen ? 'rotate-180' : ''}`} />
            </button>
            {moreOpen && (
              <div className="absolute left-0 top-[44px] z-30 w-64 bg-cream-50 border border-cream-300 rounded-xl shadow-lg py-2" data-testid="more-filters-popover">
                <button
                  onClick={() => { setMoreOpen(false); setDiagnoseModal({ q: searchQuery || '', test_password: '', loading: false, data: null, error: null }); }}
                  className="w-full text-left px-4 py-2.5 text-sm text-sand-700 hover:bg-cream-200 flex items-center gap-2.5"
                  data-testid="open-login-diagnose"
                >
                  <Stethoscope className="w-4 h-4 text-terra" />
                  Login-Diagnose
                </button>
                <button
                  onClick={() => { setMoreOpen(false); reseedCourses(); }}
                  className="w-full text-left px-4 py-2.5 text-sm text-sand-700 hover:bg-cream-200 flex items-center gap-2.5"
                  data-testid="reseed-courses-btn"
                >
                  <BookOpen className="w-4 h-4 text-terra" />
                  Kurs-Daten aktualisieren
                </button>
              </div>
            )}
          </div>

          <span className="ml-auto font-mono text-[10.5px] uppercase font-semibold text-sand-500" style={{ letterSpacing: '0.16em' }} data-testid="user-count">
            {filteredUsers.length} Teilnehmer
          </span>
        </div>

        {/* Liste — eine ruhige Zeile pro Teilnehmer */}
        <div className="bg-cream-100 rounded-2xl border border-cream-300 overflow-hidden">
          <div className="grid grid-cols-12 gap-3 px-5 py-2.5 border-b border-cream-300 font-mono text-[9.5px] uppercase font-semibold text-sand-500" style={{ letterSpacing: '0.16em' }}>
            <div className="col-span-4">Teilnehmer</div>
            <div className="col-span-4">Hauptkurs</div>
            <div className="col-span-1 text-right">UE</div>
            <div className="col-span-2 text-right">Letzte Aktivität</div>
            <div className="col-span-1" />
          </div>

          {filteredUsers.length === 0 ? (
            <div className="text-center py-14 text-sand-500">
              <p className="font-display italic text-[17px]">{searchQuery ? 'Keine Treffer — prüfe die Schreibweise.' : 'Keine Teilnehmer in dieser Ansicht.'}</p>
            </div>
          ) : (
            filteredUsers.map((u) => {
              const e = enrich(u);
              return (
                <button
                  key={u.id}
                  onClick={() => setDrawerUserId(u.id)}
                  className={`w-full grid grid-cols-12 gap-3 px-5 py-3.5 items-center text-left border-b border-cream-200 last:border-0 hover:bg-cream-200 transition-colors ${u.archived ? 'opacity-55' : ''}`}
                  data-testid={`user-row-${u.id}`}
                >
                  {/* Name */}
                  <div className="col-span-4 flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center font-display text-[14px] flex-shrink-0" style={{ backgroundColor: u.archived ? '#B4AA9C' : '#E07A5F', color: '#F5F0EA' }}>
                      {u.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div className="min-w-0">
                      <p className="text-[14.5px] text-espresso font-medium truncate">
                        {u.name}
                        {u.archived && <span className="ml-2 font-mono text-[8.5px] uppercase text-sand-500" style={{ letterSpacing: '0.14em' }}>Archiviert</span>}
                      </p>
                      <p className="text-[11px] text-sand-500 truncate">@{u.username}</p>
                    </div>
                  </div>

                  {/* Hauptkurs mit EINEM Balken */}
                  <div className="col-span-4 min-w-0">
                    {e.main ? (
                      <div className="min-w-0">
                        <div className="flex items-baseline justify-between gap-2">
                          <p className="text-[12.5px] text-sand-700 truncate">{getShortCourseTitle(e.main.course_title)}</p>
                          <span className="text-[11px] text-sand-500 flex-shrink-0">
                            {Math.round(e.main.progress_percentage)} %
                            {e.pCourses.length > 1 && <span className="ml-1.5 text-sand-400">+{e.pCourses.length - 1} weitere</span>}
                          </span>
                        </div>
                        <div className="mt-1 h-[3px] rounded-full overflow-hidden" style={{ backgroundColor: '#E0D6CA' }}>
                          <div className="h-full rounded-full" style={{ width: `${Math.min(100, e.main.progress_percentage)}%`, backgroundColor: e.main.progress_percentage >= 100 ? '#D89232' : '#E07A5F' }} />
                        </div>
                      </div>
                    ) : (
                      <span className="text-[12px] text-sand-400 italic">Keine Kurse</span>
                    )}
                  </div>

                  {/* UE */}
                  <div className="col-span-1 text-right">
                    <span className="font-display text-[16px] text-espresso">{e.ue}</span>
                  </div>

                  {/* Letzte Aktivität */}
                  <div className="col-span-2 text-right">
                    <span className="text-[12px] text-sand-600">{relActivity(e.minStuck)}</span>
                  </div>

                  {/* Status-Punkt */}
                  <div className="col-span-1 flex justify-end">
                    <span
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: STATUS[e.status].color }}
                      title={STATUS[e.status].label}
                      data-testid={`status-dot-${u.id}`}
                    />
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* ============ SEITENPANEL (Drawer, 480px) ============ */}
        {drawerUser && createPortal((() => {
          const e = enrich(drawerUser);
          return (
            <>
              <div className="fixed inset-0 bg-espresso/50 backdrop-blur-[2px] z-40" onClick={() => setDrawerUserId(null)} data-testid="drawer-overlay" />
              <div className="fixed top-0 right-0 bottom-0 z-50 w-full max-w-[480px] bg-cream-50 border-l border-cream-300 shadow-2xl flex flex-col animate-slide-in-right" data-testid="user-drawer">
                {/* Drawer-Kopf */}
                <div className="px-7 pt-7 pb-5 border-b border-cream-300 flex items-start justify-between gap-4">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center font-display text-[18px] flex-shrink-0" style={{ backgroundColor: '#E07A5F', color: '#F5F0EA' }}>
                      {drawerUser.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div className="min-w-0">
                      <h2 className="font-display text-[24px] text-espresso leading-tight truncate">{drawerUser.name}</h2>
                      <p className="text-[12px] text-sand-500 flex items-center gap-2">
                        @{drawerUser.username}
                        <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ backgroundColor: STATUS[e.status].color }} />
                        <span>{STATUS[e.status].label}</span>
                      </p>
                    </div>
                  </div>
                  <button onClick={() => setDrawerUserId(null)} className="text-sand-500 hover:text-espresso p-1 flex-shrink-0" data-testid="drawer-close">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto px-7 py-6 space-y-8">
                  {/* Persönliche Daten */}
                  <div data-testid="drawer-personal">
                    <p className="font-mono text-[9.5px] uppercase font-semibold mb-3 pb-2 border-b border-cream-300" style={{ letterSpacing: '0.22em', color: '#8F650F' }}>Persönliche Daten</p>
                    <div className="space-y-2 text-[13.5px]">
                      <div className="flex justify-between gap-3">
                        <span className="text-sand-500">Name</span>
                        <button onClick={() => updateUserName(drawerUser.id, drawerUser.name)} className="text-espresso hover:text-terra text-right" data-testid="drawer-edit-name">{drawerUser.name || '—'} ✎</button>
                      </div>
                      <div className="flex justify-between gap-3">
                        <span className="text-sand-500">E-Mail</span>
                        <button onClick={() => updateUserEmail(drawerUser)} className="text-espresso hover:text-terra text-right truncate" data-testid="drawer-edit-email">{drawerUser.email || '+ hinzufügen'} ✎</button>
                      </div>
                      <div className="flex justify-between gap-3">
                        <span className="text-sand-500">Geburtsdatum</span>
                        <button onClick={() => updateUserBirthDate(drawerUser.id, drawerUser.birth_date)} className="text-espresso hover:text-terra text-right" data-testid="drawer-edit-birthdate">{formatBirthDate(drawerUser.birth_date) || '—'} ✎</button>
                      </div>
                      <div className="flex justify-between gap-3 items-center">
                        <span className="text-sand-500">Berufsrolle</span>
                        <select
                          value={drawerUser.profession || ''}
                          onChange={(ev) => updateUserProfession(drawerUser.id, ev.target.value)}
                          className="px-2 py-1 bg-cream-100 border border-cream-300 rounded-lg text-[12px] text-sand-800"
                          data-testid="drawer-profession"
                        >
                          <option value="">— nicht gesetzt —</option>
                          <optgroup label="Leitung">
                            <option value="pdl">PDL</option>
                            <option value="leitung">Leitung</option>
                            <option value="wbl">WBL</option>
                            <option value="praxisanleitung">Praxisanleitung</option>
                            <option value="heimleitung">Heimleitung</option>
                            <option value="inhaber">Inhaber:in</option>
                          </optgroup>
                          <optgroup label="Pflege">
                            <option value="fachkraft">Pflegefachkraft</option>
                            <option value="helfer-erfahren">Pflegehelfer (erfahren)</option>
                            <option value="helfer-neu">Pflegehelfer (neu)</option>
                            <option value="ungelernt">Ungelernt</option>
                          </optgroup>
                        </select>
                      </div>
                      <div className="flex justify-between gap-3">
                        <span className="text-sand-500">Lernzeit gesamt</span>
                        <span className="text-espresso">{e.p?.total_learning_time_minutes || 0} Min · {e.ue} UE</span>
                      </div>
                    </div>
                  </div>

                  {/* Kurse & Fortschritte */}
                  <div data-testid="drawer-courses">
                    <div className="flex items-center justify-between mb-3 pb-2 border-b border-cream-300">
                      <p className="font-mono text-[9.5px] uppercase font-semibold" style={{ letterSpacing: '0.22em', color: '#8F650F' }}>Kurse &amp; Fortschritt</p>
                      <button
                        onClick={() => downloadArbeitgeberBericht(drawerUser)}
                        className="text-[11px] font-semibold hover:underline underline-offset-2 whitespace-nowrap"
                        style={{ color: '#E07A5F' }}
                        title="Kurzauskunft zum Fortbildungsstand (Lernstand, letzte Aktivität) als PDF"
                        data-testid="drawer-arbeitgeber-bericht-btn"
                      >
                        Arbeitgeber-Bericht (PDF) ↓
                      </button>
                    </div>
                    {(drawerUser.assigned_courses || []).length === 0 && (
                      <p className="text-[13px] text-sand-500 italic">Noch keine Kurse zugewiesen.</p>
                    )}
                    <div className="space-y-4">
                      {(drawerUser.assigned_courses || []).map((cid) => {
                        const course = courses.find((c) => c.id === cid);
                        const pc = e.pCourses.find((c) => c.course_id === cid);
                        const pct = pc ? Math.round(pc.progress_percentage) : 0;
                        return (
                          <div key={cid} className="group">
                            <div className="flex items-baseline justify-between gap-2">
                              <p className="text-[13px] text-espresso leading-snug flex-1">{course?.title || cid}</p>
                              <span className="text-[12px] text-sand-500 flex-shrink-0">{pct} %</span>
                            </div>
                            <div className="mt-1.5 h-[3px] rounded-full overflow-hidden" style={{ backgroundColor: '#E0D6CA' }}>
                              <div className="h-full rounded-full" style={{ width: `${Math.min(100, pct)}%`, backgroundColor: pct >= 100 ? '#D89232' : '#E07A5F' }} />
                            </div>
                            <div className="flex items-center gap-4 mt-1.5">
                              {AZAV_VARIANT_COURSES.includes(cid) && (() => {
                                const v = drawerUser.course_variants?.[cid] || 'bg';
                                const seg = (val, label) => (
                                  <button
                                    key={val}
                                    onClick={() => v !== val && setAssignmentVariant(drawerUser.id, cid, val)}
                                    className="text-[10.5px] font-semibold px-2 py-0.5 transition-colors whitespace-nowrap"
                                    style={v === val
                                      ? { color: '#FFF', backgroundColor: '#E07A5F' }
                                      : { color: '#8A8272', backgroundColor: 'transparent' }}
                                    data-testid={`drawer-variant-${cid}-${val}`}
                                  >
                                    {label}
                                  </button>
                                );
                                return (
                                  <span className="inline-flex rounded-full border overflow-hidden" style={{ borderColor: '#E07A5F' }} title="Lernform wählen — Lernstand bleibt erhalten" data-testid={`drawer-variant-${cid}`}>
                                    {seg('bg', 'BG-Maßnahme')}
                                    {seg('selbstlernen', 'Selbstlernen')}
                                  </span>
                                );
                              })()}
                              {pc && pc.days_stuck >= 14 && pct < 100 && (
                                <span className="text-[11px]" style={{ color: '#E07A5F' }}>seit {pc.days_stuck} Tagen inaktiv</span>
                              )}
                              {pc && pct >= 100 && !pc.certificate_issued && (
                                <button onClick={() => issueCertificate(drawerUser, cid, course?.title)} className="text-[11.5px] font-semibold hover:underline underline-offset-2" style={{ color: '#E07A5F' }} data-testid={`drawer-issue-cert-${cid}`}>
                                  Bescheinigung ausstellen ↓
                                </button>
                              )}
                              {pc && pct >= 100 && pc.certificate_issued && (
                                <span className="text-[11px]" style={{ color: '#8F650F' }}>✓ Bescheinigt</span>
                              )}
                              <button
                                onClick={() => setBackfillModal({ userId: drawerUser.id, userName: drawerUser.name || drawerUser.username, courseId: cid, courseTitle: course?.title || cid })}
                                className="text-[11px] text-sand-500 hover:text-terra"
                                data-testid={`drawer-backfill-${cid}`}
                              >
                                Nachtragen
                              </button>
                              <button
                                onClick={() => setUnlockModal({ userId: drawerUser.id, userName: drawerUser.name || drawerUser.username, courseId: cid, courseTitle: course?.title || cid })}
                                className="text-[11px] text-sand-500 hover:text-terra"
                                data-testid={`drawer-unlock-${cid}`}
                              >
                                Freischalten
                              </button>
                              <button onClick={() => unassignCourse(drawerUser.id, cid)} className="text-[11px] text-sand-500 hover:text-terra ml-auto" data-testid={`drawer-unassign-${cid}`}>
                                Entfernen
                              </button>
                            </div>
                            {/* Kurs beendet + Bearbeitungsbestätigung */}
                            {(drawerUser.course_variants?.[cid] !== 'bg') && (() => {
                              const entry = mlData.entries.find((en) => en.course_id === cid);
                              const openReq = mlData.open_requests.find((rq) => rq.course_id === cid);
                              return (
                                <div className="flex flex-wrap items-center gap-3 mt-1.5" data-testid={`drawer-ml-${cid}`}>
                                  {entry ? (
                                    <>
                                      <span className="text-[11px] font-semibold" style={{ color: '#2F7A3D' }}>
                                        ✓ Bestätigung unterschrieben am {new Date(entry.signed_at).toLocaleDateString('de-DE')}
                                      </span>
                                      <button onClick={() => downloadBestaetigung(entry)}
                                        className="text-[11px] font-semibold hover:underline underline-offset-2" style={{ color: '#E07A5F' }}
                                        data-testid={`drawer-ml-pdf-${cid}`}>
                                        PDF ↓
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      <button onClick={() => kursBeendet(drawerUser, cid, course?.title || cid)}
                                        className="text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full border transition-colors hover:text-cream-50"
                                        style={{ borderColor: '#E07A5F', color: '#E07A5F' }}
                                        onMouseEnter={(ev) => { ev.target.style.backgroundColor = '#E07A5F'; ev.target.style.color = '#FBF7F0'; }}
                                        onMouseLeave={(ev) => { ev.target.style.backgroundColor = 'transparent'; ev.target.style.color = '#E07A5F'; }}
                                        data-testid={`drawer-kurs-beendet-${cid}`}>
                                        Kurs beendet ✓
                                      </button>
                                      {openReq && (
                                        <span className="text-[11px]" style={{ color: '#8F650F' }}>
                                          Unterschrift angefordert am {new Date(openReq.requested_at).toLocaleDateString('de-DE')} — noch offen
                                        </span>
                                      )}
                                    </>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex items-center gap-2 mt-4">
                      <select
                        className="flex-1 px-3 py-2 bg-cream-100 border border-cream-300 rounded-xl text-[12.5px] text-sand-800"
                        onChange={(ev) => { if (ev.target.value) { const [cid, variant] = ev.target.value.split('::'); assignCourse(drawerUser.id, cid, variant || null); ev.target.value = ''; } }}
                        defaultValue=""
                        data-testid="drawer-assign-course"
                      >
                        <option value="">+ Kurs zuweisen…</option>
                        {courses.filter((c) => !drawerUser.assigned_courses?.includes(c.id)).flatMap((c) => (
                          AZAV_VARIANT_COURSES.includes(c.id)
                            ? [
                                <option key={`${c.id}-bg`} value={`${c.id}::bg`}>{c.title} — BG-Maßnahme (mit Tagesmaterial)</option>,
                                <option key={`${c.id}-sl`} value={`${c.id}::selbstlernen`}>{c.title} — Selbstlernen (E-Learning)</option>,
                              ]
                            : [<option key={c.id} value={c.id}>{c.title}</option>]
                        ))}
                      </select>
                      <button onClick={() => assignAllJahresfortbildung(drawerUser.id)} className="px-3 py-2 text-[11.5px] bg-cream-100 border border-cream-300 rounded-xl text-sand-600 hover:bg-cream-200 whitespace-nowrap">
                        4× Jahresfortbildung
                      </button>
                      <button onClick={() => assignAllExpertenstandards(drawerUser.id)} className="px-3 py-2 text-[11.5px] bg-cream-100 border border-cream-300 rounded-xl text-sand-600 hover:bg-cream-200 whitespace-nowrap" data-testid="drawer-assign-all-es">
                        10× Expertenstandards
                      </button>
                    </div>
                  </div>

                  {/* Eingereichte Aufgaben — Schnellkorrektur */}
                  <div data-testid="drawer-tasks">
                    <p className="font-mono text-[9.5px] uppercase font-semibold mb-3 pb-2 border-b border-cream-300" style={{ letterSpacing: '0.22em', color: '#8F650F' }}>Eingereichte Aufgaben</p>
                    <DrawerTaskFeedback userId={drawerUser.id} getAuthHeader={getAuthHeader} onFeedbackSent={loadData} />
                  </div>

                  {/* Aktionen */}
                  <div data-testid="drawer-actions">
                    <p className="font-mono text-[9.5px] uppercase font-semibold mb-3 pb-2 border-b border-cream-300" style={{ letterSpacing: '0.22em', color: '#8F650F' }}>Aktionen</p>
                    <div className="grid grid-cols-2 gap-2">
                      <button onClick={() => sendReminder(drawerUser)} className="px-3 py-2.5 rounded-xl text-[12.5px] font-medium text-left transition-colors" style={{ border: '1px solid rgba(169,84,47,0.4)', color: '#E07A5F' }} data-testid="drawer-send-reminder">
                        Erinnerung senden
                      </button>
                      <button onClick={() => resetPassword(drawerUser)} className="px-3 py-2.5 rounded-xl text-[12.5px] font-medium text-left border border-cream-300 text-sand-700 hover:bg-cream-200 transition-colors" data-testid="drawer-reset-password">
                        Passwort zurücksetzen
                      </button>
                      <button onClick={() => sendWelcomeMailToExistingUser(drawerUser)} className="px-3 py-2.5 rounded-xl text-[12.5px] font-medium text-left border border-cream-300 text-sand-700 hover:bg-cream-200 transition-colors" data-testid="drawer-welcome-mail">
                        Willkommens-Mail senden
                      </button>
                      <button onClick={() => exportTasks(drawerUser, 'xlsx')} className="px-3 py-2.5 rounded-xl text-[12.5px] font-medium text-left border border-cream-300 text-sand-700 hover:bg-cream-200 transition-colors" data-testid="drawer-export-xlsx">
                        Aufgaben-Export (Excel)
                      </button>
                      <button onClick={() => exportTasks(drawerUser, 'pdf')} className="px-3 py-2.5 rounded-xl text-[12.5px] font-medium text-left border border-cream-300 text-sand-700 hover:bg-cream-200 transition-colors" data-testid="drawer-export-pdf">
                        Aufgaben-Export (PDF)
                      </button>
                      <button onClick={() => { setDiagnoseModal({ q: drawerUser.username, test_password: '', loading: false, data: null, error: null }); }} className="px-3 py-2.5 rounded-xl text-[12.5px] font-medium text-left border border-cream-300 text-sand-700 hover:bg-cream-200 transition-colors" data-testid="drawer-login-diagnose">
                        Login-Diagnose
                      </button>
                    </div>
                  </div>

                  {/* Verwaltung */}
                  <div className="pt-2 pb-4 flex items-center gap-4 border-t border-cream-300">
                    {drawerUser.archived ? (
                      <button onClick={() => unarchiveUser(drawerUser.id, drawerUser.name)} className="text-[12.5px] font-semibold hover:underline underline-offset-2" style={{ color: '#8F650F' }} data-testid="drawer-unarchive">
                        Reaktivieren
                      </button>
                    ) : (
                      <button onClick={() => archiveUser(drawerUser.id, drawerUser.name)} className="text-[12.5px] text-sand-600 hover:text-terra" data-testid="drawer-archive">
                        Archivieren
                      </button>
                    )}
                    <button onClick={() => deleteUser(drawerUser.id, drawerUser.name)} className="text-[12.5px] text-sand-500 hover:text-terra ml-auto" data-testid="drawer-delete">
                      Endgültig löschen
                    </button>
                  </div>
                </div>
              </div>
            </>
          );
        })(), document.body)}


        {/* Modul-Freischaltung */}
        {unlockModal && (
          <AdminModuleUnlockModal
            userId={unlockModal.userId}
            userName={unlockModal.userName}
            courseId={unlockModal.courseId}
            courseTitle={unlockModal.courseTitle}
            getAuthHeader={getAuthHeader}
            onClose={() => setUnlockModal(null)}
          />
        )}

        {/* Create User Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-cream-100 rounded-2xl border border-cream-300 w-full max-w-md max-h-[90vh] flex flex-col animate-fade-in">
              {/* Sticky Header */}
              <div className="flex items-center justify-between p-6 border-b border-cream-300">
                <h2 className="text-xl font-bold text-terra">Neuer Benutzer</h2>
                <button onClick={() => setShowCreateModal(false)} className="text-sand-500 hover:text-sand-500"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={createUser} className="flex flex-col flex-1 overflow-hidden">
                {/* Scrollable body */}
                <div className="p-6 space-y-4 overflow-y-auto flex-1">
                  <div>
                    <label className="block text-sm text-sand-500 mb-1">Name</label>
                    <input autoFocus type="text" required value={newUser.name} onChange={e => setNewUser({...newUser, name: e.target.value})} className="w-full px-4 py-2.5 bg-cream-200 border border-cream-300 rounded-xl text-sand-800 focus:outline-none focus:border-terra" placeholder="Vor- und Nachname" />
                  </div>
                  <div>
                    <label className="block text-sm text-sand-500 mb-1">Benutzername</label>
                    <input type="text" required value={newUser.username} onChange={e => setNewUser({...newUser, username: e.target.value})} className="w-full px-4 py-2.5 bg-cream-200 border border-cream-300 rounded-xl text-sand-800 focus:outline-none focus:border-terra" placeholder="benutzername" />
                  </div>
                  <div>
                    <label className="block text-sm text-sand-500 mb-1">Passwort</label>
                    <input type="text" required value={newUser.password} onChange={e => setNewUser({...newUser, password: e.target.value})} className="w-full px-4 py-2.5 bg-cream-200 border border-cream-300 rounded-xl text-sand-800 focus:outline-none focus:border-terra" placeholder="Passwort" />
                  </div>
                  <div>
                    <label className="block text-sm text-sand-500 mb-1">Geburtsdatum (optional)</label>
                    <input type="date" value={newUser.birth_date} onChange={e => setNewUser({...newUser, birth_date: e.target.value})} className="w-full px-4 py-2.5 bg-cream-200 border border-cream-300 rounded-xl text-sand-800 focus:outline-none focus:border-terra" />
                  </div>
                  
                  <div>
                    <label className="block text-sm text-sand-500 mb-1">E-Mail-Adresse <span className="text-sand-500 text-xs">(für Erinnerungs-Mails)</span></label>
                    <input type="email" value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})} placeholder="anna@example.de" className="w-full px-4 py-2.5 bg-cream-200 border border-cream-300 rounded-xl text-sand-800 focus:outline-none focus:border-terra" />
                  </div>

                  {/* Willkommens-E-Mail Checkbox */}
                  {newUser.email?.trim() && (
                    <label className={`flex items-start gap-3 px-3.5 py-3 rounded-xl border-2 cursor-pointer transition-colors ${sendWelcomeEmail ? 'bg-sage/10 border-sage/40' : 'bg-cream-100 border-cream-300 hover:bg-cream-200'}`} data-testid="welcome-email-toggle">
                      <input
                        type="checkbox"
                        checked={sendWelcomeEmail}
                        onChange={(e) => setSendWelcomeEmail(e.target.checked)}
                        className="mt-0.5 w-4 h-4 rounded accent-[#7a8b6a]"
                      />
                      <div className="flex-1 text-xs">
                        <div className="font-semibold text-sand-800 flex items-center gap-1.5">
                          ✉️ Willkommens-Mail jetzt versenden
                        </div>
                        <div className="text-sand-500 mt-0.5">
                          {newUser.name && newUser.email
                            ? <>An <strong>{newUser.email}</strong> mit Benutzername + Passwort, Login-Link und ersten Schritten.</>
                            : 'An die hinterlegte Adresse mit Login-Daten und Onboarding-Anleitung.'}
                        </div>
                      </div>
                    </label>
                  )}

                  {/* Kurse direkt zuweisen — spart einen extra Schritt */}
                  <div>
                    <label className="flex items-center justify-between text-sm text-sand-500 mb-1">
                      <span>Kurse zuweisen <span className="text-sand-500 text-xs">(optional, mehrfach wählbar)</span></span>
                      <span className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setNewUserCourses((prev) => [...new Set([...prev, ...courses.filter((c) => c.id.startsWith('course-es-')).map((c) => c.id)])])}
                          className="text-[10px] text-sand-600 bg-cream-200 hover:bg-cream-300 px-2 py-0.5 rounded-full"
                          data-testid="new-user-select-all-es"
                        >
                          + Alle 10 Expertenstandards
                        </button>
                        {newUserCourses.length > 0 && (
                          <button type="button" onClick={() => setNewUserCourses([])} className="text-[10px] text-terra hover:underline">Auswahl löschen</button>
                        )}
                      </span>
                    </label>
                    <div className="max-h-44 overflow-y-auto bg-cream-50 border border-cream-300 rounded-xl p-2 space-y-1" data-testid="new-user-courses-select">
                      {courses.length === 0 ? (
                        <p className="text-xs text-sand-400 py-2 text-center">Keine Kurse geladen</p>
                      ) : (
                        courses.map(c => {
                          const checked = newUserCourses.includes(c.id);
                          return (
                            <label key={c.id} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer transition-colors ${checked ? 'bg-terra/10 border border-terra/30' : 'hover:bg-cream-200 border border-transparent'}`}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleNewUserCourse(c.id)}
                                className="w-3.5 h-3.5 rounded bg-cream-200 border-cream-300 accent-[#D4C4B0]"
                              />
                              <span className={`text-xs truncate ${checked ? 'text-sand-800' : 'text-sand-500'}`}>{getShortCourseTitle(c.title) || c.title}</span>
                            </label>
                          );
                        })
                      )}
                    </div>
                    {newUserCourses.length > 0 && (
                      <p className="text-[10px] text-terra mt-1.5">{newUserCourses.length} Kurs(e) werden nach Erstellung sofort zugewiesen</p>
                    )}
                  </div>
                </div>
                {/* Sticky Footer */}
                <div className="flex gap-3 p-6 border-t border-cream-300 bg-cream-100">
                  <button type="button" onClick={() => setShowCreateModal(false)} className="flex-1 px-4 py-2.5 bg-cream-300 rounded-xl text-sand-500 hover:bg-cream-300">Abbrechen</button>
                  <button type="submit" disabled={creating} className="flex-1 px-4 py-2.5 bg-gradient-to-r from-terra to-terra-dark text-sand-800 rounded-xl font-medium hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2">
                    {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                    Erstellen
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Backfill-Progress Modal */}
        {backfillModal && (
          <BackfillProgressModal
            data={backfillModal}
            onClose={() => setBackfillModal(null)}
            onSuccess={() => { setBackfillModal(null); loadData(); }}
          />
        )}

        {/* Login-Diagnose Modal */}
        {diagnoseModal && (
          <LoginDiagnoseModal
            initial={diagnoseModal}
            onClose={() => setDiagnoseModal(null)}
            getAuthHeader={getAuthHeader}
          />
        )}
      </div>
    </Layout>
  );
};


// =====================================================================
// BackfillProgressModal — Admin tool: nachträglich Module als erledigt markieren
// =====================================================================
const BackfillProgressModal = ({ data, onClose, onSuccess }) => {
  const { userId, userName, courseId, courseTitle } = data;
  const [upToModule, setUpToModule] = useState(5);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [answerText, setAnswerText] = useState('');
  const [reason, setReason] = useState('Administrative Nachtragung');
  const [fillTasks, setFillTasks] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Sensible defaults: this week
  useEffect(() => {
    const today = new Date();
    const weekAgo = new Date(today);
    weekAgo.setDate(today.getDate() - 6);
    const fmt = (d) => d.toISOString().slice(0, 10);
    setStartDate(fmt(weekAgo));
    setEndDate(fmt(today));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!startDate || !endDate) {
      toast.error('Bitte Start- und End-Datum wählen');
      return;
    }
    if (!upToModule || upToModule < 1) {
      toast.error('Bitte gültige Modul-Nummer angeben');
      return;
    }
    if (!window.confirm(
      `Wirklich für „${userName}"\nim Kurs „${courseTitle}"\n` +
      `Module 1 bis ${upToModule} rückwirkend zwischen ${startDate} und ${endDate}\n` +
      `als erledigt markieren?\n\n` +
      `Dies legt Lernzeit, Quiz-Bestanden (100%) und${fillTasks ? ' alle Aufgaben-Antworten' : ''} an. Idempotent — kann mehrfach ausgeführt werden.`
    )) return;

    setSubmitting(true);
    try {
      const params = new URLSearchParams({
        up_to_module_order: String(upToModule),
        start_date: startDate,
        end_date: endDate,
        fill_task_answers: String(fillTasks),
        reason: reason || 'Administrative Nachtragung',
      });
      if (answerText.trim()) params.set('answer_text', answerText.trim());
      const res = await axios.post(
        `${API}/admin/users/${userId}/courses/${courseId}/backfill-progress?${params.toString()}`
      );
      setResult(res.data);
      toast.success(`${res.data.modules_processed} Module nachgetragen — ${res.data.tasks_filled} Aufgaben + ${res.data.quizzes_passed} Quizzes.`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Unbekannter Fehler';
      toast.error(`Backfill fehlgeschlagen: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-espresso/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in"
      onClick={onClose}
      data-testid="backfill-modal"
    >
      <div
        className="bg-cream-50 rounded-2xl border border-cream-300 w-full max-w-lg max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-cream-300">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-terra font-semibold mb-0.5">
              Fortschritt rückwirkend nachtragen
            </div>
            <h2 className="font-display text-2xl text-espresso leading-tight tracking-wide">
              {userName}
            </h2>
            <p className="text-xs text-sand-600 mt-0.5 line-clamp-1">{courseTitle}</p>
          </div>
          <button onClick={onClose} className="text-sand-500 hover:text-espresso p-1" data-testid="backfill-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        {!result ? (
          <form onSubmit={submit} className="flex flex-col flex-1 overflow-hidden">
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm text-sand-700 mb-1 font-medium">
                  Bis einschließlich Modul Nr.
                </label>
                <input
                  type="number"
                  min={1}
                  required
                  value={upToModule}
                  onChange={(e) => setUpToModule(parseInt(e.target.value || '0', 10))}
                  className="w-full px-4 py-2.5 bg-cream-200 border border-cream-300 rounded-xl text-sand-800 focus:outline-none focus:border-terra"
                  data-testid="backfill-modules"
                />
                <p className="text-[11px] text-sand-500 mt-1">
                  Es werden alle Module mit Reihenfolge 1 bis {upToModule || 'N'} bearbeitet.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-sand-700 mb-1 font-medium">Von (Datum)</label>
                  <input
                    type="date"
                    required
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-3 py-2.5 bg-cream-200 border border-cream-300 rounded-xl text-sand-800 focus:outline-none focus:border-terra"
                    data-testid="backfill-start"
                  />
                </div>
                <div>
                  <label className="block text-sm text-sand-700 mb-1 font-medium">Bis (Datum)</label>
                  <input
                    type="date"
                    required
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-3 py-2.5 bg-cream-200 border border-cream-300 rounded-xl text-sand-800 focus:outline-none focus:border-terra"
                    data-testid="backfill-end"
                  />
                </div>
              </div>
              <label className="flex items-start gap-3 px-3.5 py-3 rounded-xl border border-cream-300 bg-cream-100 cursor-pointer hover:bg-cream-200 transition-colors">
                <input
                  type="checkbox"
                  checked={fillTasks}
                  onChange={(e) => setFillTasks(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded accent-[#C2474A]"
                  data-testid="backfill-fill-tasks"
                />
                <div className="text-xs">
                  <div className="font-semibold text-sand-800">Aufgaben-Antworten ausfüllen</div>
                  <div className="text-sand-600 mt-0.5">Für jede Aufgabe wird automatisch eine Backfill-Antwort hinterlegt.</div>
                </div>
              </label>
              <div>
                <label className="block text-sm text-sand-700 mb-1 font-medium">
                  Antwort-Text <span className="text-sand-500 text-xs font-normal">(optional)</span>
                </label>
                <textarea
                  rows={2}
                  value={answerText}
                  onChange={(e) => setAnswerText(e.target.value)}
                  placeholder="Default: 'Antwort administrativ nachgetragen — vorherige Bearbeitung außerhalb der Plattform dokumentiert.'"
                  className="w-full px-3 py-2 bg-cream-200 border border-cream-300 rounded-xl text-sand-800 focus:outline-none focus:border-terra text-xs"
                  data-testid="backfill-answer-text"
                />
              </div>
              <div>
                <label className="block text-sm text-sand-700 mb-1 font-medium">
                  Begründung <span className="text-sand-500 text-xs font-normal">(Audit-Log)</span>
                </label>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full px-3 py-2 bg-cream-200 border border-cream-300 rounded-xl text-sand-800 focus:outline-none focus:border-terra text-xs"
                  data-testid="backfill-reason"
                />
              </div>
              <div className="bg-peach-50 border border-peach-200 rounded-xl px-4 py-3 text-xs text-sand-700">
                <strong>Hinweis:</strong> Diese Aktion ist <strong>idempotent</strong> — bei erneutem Ausführen werden vorhandene Einträge nur aktualisiert. Höhere Lernzeit-Werte werden NIE überschrieben. Alle Schritte werden im Audit-Log protokolliert.
              </div>
            </div>
            <div className="px-6 py-4 bg-cream-100 border-t border-cream-300 flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-full border border-sand-300 text-sand-700 hover:bg-cream-200 text-sm font-medium"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-5 py-2 rounded-full bg-terra hover:bg-terra-dark text-cream-50 font-medium text-sm inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="backfill-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <History className="w-4 h-4" />}
                Nachtragen
              </button>
            </div>
          </form>
        ) : (
          <div className="flex flex-col flex-1 overflow-hidden">
            <div className="p-6 space-y-3 overflow-y-auto flex-1">
              <div className="bg-sage/10 border border-sage/30 rounded-xl px-4 py-3 text-sm text-sand-800">
                ✅ <strong>{result.modules_processed}</strong> Module nachgetragen
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-cream-100 border border-cream-300 rounded-xl px-3 py-3">
                  <div className="font-display text-2xl text-espresso">{result.modules_processed}</div>
                  <div className="text-[10px] uppercase tracking-wider text-sand-600 font-semibold">Module</div>
                </div>
                <div className="bg-cream-100 border border-cream-300 rounded-xl px-3 py-3">
                  <div className="font-display text-2xl text-espresso">{result.tasks_filled}</div>
                  <div className="text-[10px] uppercase tracking-wider text-sand-600 font-semibold">Aufgaben</div>
                </div>
                <div className="bg-cream-100 border border-cream-300 rounded-xl px-3 py-3">
                  <div className="font-display text-2xl text-espresso">{result.quizzes_passed}</div>
                  <div className="text-[10px] uppercase tracking-wider text-sand-600 font-semibold">Quizzes</div>
                </div>
              </div>
              <div className="text-xs text-sand-600">
                Lernzeit gutgeschrieben: <strong>{Math.round((result.learning_time_seconds_added || 0) / 60)} Min</strong>
                {' '}({Math.round((result.learning_time_seconds_added || 0) / 2700)} UE)
              </div>
              <details className="bg-cream-100 border border-cream-300 rounded-xl px-4 py-2 text-xs">
                <summary className="cursor-pointer font-semibold text-sand-700">Modul-Details ({result.modules?.length || 0})</summary>
                <ul className="mt-2 space-y-1 text-sand-600">
                  {(result.modules || []).map((m) => (
                    <li key={m.module_id} className="leading-snug">
                      <strong>M{m.module_order}:</strong> {m.module_title} —{' '}
                      <span className="text-sand-500">{(m.completed_at || '').slice(0, 10)}</span>
                      {' '}({m.tasks_filled} Aufgaben, {m.quiz_questions_passed} Quiz-Q)
                    </li>
                  ))}
                </ul>
              </details>
            </div>
            <div className="px-6 py-4 bg-cream-100 border-t border-cream-300 flex justify-end">
              <button
                onClick={onSuccess}
                className="px-5 py-2 rounded-full bg-terra hover:bg-terra-dark text-cream-50 font-medium text-sm"
                data-testid="backfill-done"
              >
                Fertig
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsersPage;


// =====================================================================
// LoginDiagnoseModal — prüft, warum ein Teilnehmer sich nicht einloggen kann
// =====================================================================
const LoginDiagnoseModal = ({ initial, onClose, getAuthHeader }) => {
  const [q, setQ] = useState(initial.q || '');
  const [testPassword, setTestPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const runDiagnose = async () => {
    if (!q.trim()) { setError('Bitte Benutzername oder Name eingeben.'); return; }
    setLoading(true); setError(null); setData(null);
    try {
      const params = new URLSearchParams({ q: q.trim() });
      if (testPassword) params.append('test_password', testPassword);
      const res = await axios.get(`${API}/admin/diagnose-login?${params.toString()}`, getAuthHeader());
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Fehler beim Abruf');
    }
    setLoading(false);
  };

  const renderStatus = (label, ok, okText, badText, neutralText) => (
    <div className="flex items-center justify-between py-1.5 border-b border-cream-200 last:border-0 text-xs">
      <span className="text-sand-500">{label}</span>
      <span className={`font-medium ${ok === true ? 'text-sage-dark' : ok === false ? 'text-coral-dark' : 'text-sand-700'}`}>
        {ok === true ? okText : ok === false ? badText : neutralText}
      </span>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-cream-100 rounded-2xl border border-cream-300 w-full max-w-2xl max-h-[90vh] flex flex-col animate-fade-in">
        <div className="flex items-center justify-between p-6 border-b border-cream-300">
          <div>
            <h2 className="font-display text-2xl uppercase tracking-wide text-sand-800">Login-Diagnose</h2>
            <p className="text-xs text-sand-500 mt-1">Prüft, warum ein Teilnehmer sich nicht einloggen kann.</p>
          </div>
          <button onClick={onClose} className="text-sand-500 hover:text-sand-800" data-testid="diagnose-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {/* Inputs */}
          <div className="space-y-2">
            <label className="block text-xs text-sand-600 font-medium">Benutzername oder Name</label>
            <input
              autoFocus
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') runDiagnose(); }}
              placeholder="z.B. amirali"
              className="w-full px-4 py-2.5 bg-cream-50 border border-cream-300 rounded-xl text-sand-800 focus:outline-none focus:border-terra text-sm"
              data-testid="diagnose-query-input"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-xs text-sand-600 font-medium">
              Passwort testen <span className="text-sand-500 font-normal">(optional — prüft ob ein bestimmtes Passwort funktioniert)</span>
            </label>
            <input
              type="text"
              value={testPassword}
              onChange={(e) => setTestPassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') runDiagnose(); }}
              placeholder="z.B. Lernzeit2026!"
              className="w-full px-4 py-2.5 bg-cream-50 border border-cream-300 rounded-xl text-sand-800 focus:outline-none focus:border-terra text-sm font-mono"
              data-testid="diagnose-password-input"
            />
          </div>
          <button
            onClick={runDiagnose}
            disabled={loading || !q.trim()}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-terra text-sand-800 rounded-xl font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
            data-testid="diagnose-run"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Stethoscope className="w-4 h-4" />}
            {loading ? 'Prüfe...' : 'Diagnose starten'}
          </button>

          {error && <div className="p-3 bg-coral-100 border border-coral-200 rounded-xl text-xs text-espresso">{error}</div>}

          {/* Results */}
          {data && (
            <div className="space-y-3 mt-2">
              <div className="text-xs text-sand-500">
                {data.match_count === 0 ? (
                  <span className="text-coral-dark font-medium">Kein Treffer für „{data.query}&ldquo; — Benutzer existiert nicht (oder Name/Username ist anders geschrieben).</span>
                ) : (
                  <span><b>{data.match_count}</b> Treffer für „{data.query}&ldquo;:</span>
                )}
              </div>

              {data.results.map((r, idx) => {
                const canLogin = !r.archived && r.has_password && (testPassword ? r.test_password_matches : true);
                return (
                  <div key={r.id || idx} className={`border rounded-xl overflow-hidden ${canLogin ? 'border-sage-dark/40 bg-sage-50/30' : 'border-coral-200 bg-coral-100/30'}`}>
                    <div className="px-4 py-3 flex items-center justify-between bg-cream-50 border-b border-cream-200">
                      <div className="min-w-0">
                        <div className="text-sm font-bold text-sand-800 truncate">{r.name || '(kein Name)'}</div>
                        <div className="text-xs text-sand-500 font-mono">@{r.username_in_db}{r.email ? ` · ${r.email}` : ''}</div>
                      </div>
                      <div className={`text-[10px] font-bold px-2 py-1 rounded ${canLogin ? 'bg-sage-dark text-cream-50' : 'bg-coral-dark text-cream-50'}`}>
                        {canLogin ? 'LOGIN OK' : 'PROBLEM'}
                      </div>
                    </div>
                    <div className="p-4 space-y-0">
                      {renderStatus('Username exakt wie eingetippt?', r.username_input_matches_exactly, 'Ja', `Nein — gespeichert als „${r.username_in_db}"`, '—')}
                      {renderStatus('Case-insensitive Treffer?', r.username_matches_case_insensitive, 'Ja (Login funktioniert trotz Groß-/Kleinschreibung)', 'Nein', '—')}
                      {renderStatus('Passwort gesetzt?', r.has_password, 'Ja', 'Nein — Welcome-Mail mit reset_password=true erneut senden', '—')}
                      {renderStatus('Account archiviert?', !r.archived, 'Nein (aktiv)', `Ja — am ${r.archived_at ? new Date(r.archived_at).toLocaleDateString('de-DE') : '?'}${r.archived_by ? ` durch ${r.archived_by}` : ''}`, '—')}
                      {renderStatus('Kurszuweisungen', r.course_assignments_count > 0, `${r.course_assignments_count} Kurse`, 'Keine — User sieht keinen Kurs nach Login', '—')}
                      {testPassword && renderStatus('Test-Passwort funktioniert?', r.test_password_matches, 'Ja — Login mit diesem Passwort möglich', 'Nein — Passwort ist falsch', '—')}
                    </div>

                    {/* Course-Diagnostics — zeigt für jede Kurszuweisung ob Kurs+Module+Fortschritt da sind */}
                    {r.course_diagnostics && r.course_diagnostics.length > 0 && (
                      <div className="px-4 pb-4">
                        <div className="text-[11px] font-bold text-sand-700 uppercase tracking-wide mb-2 mt-1">Zugewiesene Kurse</div>
                        <div className="space-y-1.5">
                          {r.course_diagnostics.map((cd, i) => (
                            <div key={i} className={`rounded-lg p-2.5 border ${cd.looks_healthy ? 'border-sage-dark/30 bg-sage-50/30' : 'border-coral-300 bg-coral-100/50'}`}>
                              <div className="flex items-center justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                  <div className="text-xs font-medium text-sand-800 truncate">
                                    {cd.course_title || cd.course_id}
                                  </div>
                                  <div className="text-[10px] text-sand-500 font-mono truncate">{cd.course_id}</div>
                                </div>
                                <div className={`text-[10px] font-bold px-2 py-0.5 rounded whitespace-nowrap ${cd.looks_healthy ? 'bg-sage-dark text-cream-50' : 'bg-coral-dark text-cream-50'}`}>
                                  {cd.looks_healthy ? 'OK' : 'PROBLEM'}
                                </div>
                              </div>
                              <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10px] text-sand-600">
                                <div>Kurs in DB: <span className={cd.course_exists_in_db ? 'text-sage-dark font-medium' : 'text-coral-dark font-bold'}>{cd.course_exists_in_db ? 'Ja' : '❌ FEHLT'}</span></div>
                                <div>Module in DB: <span className={cd.modules_in_db > 0 ? 'text-sage-dark font-medium' : 'text-coral-dark font-bold'}>{cd.modules_in_db} / {cd.course_total_modules_expected} erwartet</span></div>
                                <div>Lernzeit-Einträge: {cd.user_has_learning_time}</div>
                                <div>Aufgaben-Abgaben: {cd.user_has_task_submissions}</div>
                                <div>Live-Sessions: {cd.user_has_live_sessions}</div>
                              </div>
                              {!cd.course_exists_in_db && (
                                <div className="mt-1.5 text-[10px] text-espresso bg-cream-50 rounded p-1.5">
                                  ⚠️ Der Kurs fehlt in der DB. Klicke oben &bdquo;Kurs-Daten aktualisieren&ldquo; — das schreibt alle Kurse aus dem Code neu in die DB (Teilnehmerdaten bleiben erhalten).
                                </div>
                              )}
                              {cd.course_exists_in_db && cd.modules_in_db < cd.course_total_modules_expected && (
                                <div className="mt-1.5 text-[10px] text-espresso bg-cream-50 rounded p-1.5">
                                  ⚠️ Nur {cd.modules_in_db} von {cd.course_total_modules_expected} Modulen in der DB. Klicke oben &bdquo;Kurs-Daten aktualisieren&ldquo;.
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Recent audit log */}
                    {r.recent_audit && r.recent_audit.length > 0 && (
                      <details className="px-4 pb-3">
                        <summary className="text-xs text-sand-500 cursor-pointer hover:text-sand-700">Letzte Account-Aktionen ({r.recent_audit.length})</summary>
                        <ul className="mt-2 space-y-1 text-[11px] text-sand-600">
                          {r.recent_audit.map((a, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <span className="font-mono text-sand-500">{a.timestamp ? new Date(a.timestamp).toLocaleString('de-DE') : '—'}</span>
                              <span className="font-medium text-sand-800">{a.action}</span>
                              <span className="text-sand-500">{a.details}</span>
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                );
              })}

              {data.hints && data.hints.length > 0 && (
                <div className="p-3 bg-cream-50 border border-cream-300 rounded-xl">
                  <div className="text-[11px] font-bold text-sand-700 uppercase tracking-wide mb-1">Hinweise</div>
                  <ul className="space-y-1 text-[11px] text-sand-600">
                    {data.hints.map((h, i) => <li key={i}>• {h}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-cream-300 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm text-sand-600 hover:text-sand-800">Schließen</button>
        </div>
      </div>
    </div>
  );
};
